import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminSubpage from "../components/AdminSubpage";
import { auctionsAPI, productsAPI } from "../services/api";
import "../components/Auctions.css";
import { Link } from "react-router-dom";

function normalizeItems(items) {
  return items.map((item, idx) => ({
    product_id: item.product_id,
    starting_price: Number(item.starting_price) || 0,
    reserve_price:
      item.reserve_price === "" || item.reserve_price == null
        ? null
        : Number(item.reserve_price),
    bid_increment: Number(item.bid_increment) || 50,
    position: idx,
  }));
}

const AuctionFormPage = () => {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(auctionId && auctionId !== "new");
  const [form, setForm] = useState({
    title: "",
    description: "",
    scheduled_at: "",
    ends_at: "",
    cover_image: "",
    is_visible_on_app: false,
    items: [],
  });
  const [productSearch, setProductSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    auctionsAPI.getById(auctionId).then((res) => {
      const d = res.data.data;
      setForm({
        title: d.title || "",
        description: d.description || "",
        scheduled_at: d.scheduled_at ? d.scheduled_at.slice(0, 16) : "",
        ends_at: d.ends_at ? d.ends_at.slice(0, 16) : "",
        cover_image: d.cover_image || "",
        is_visible_on_app: !!d.is_visible_on_app,
        items: (d.items || []).map((item) => ({
          product_id: item.product_id,
          product_title: item.title || "Product",
          starting_price: item.starting_price,
          reserve_price: item.reserve_price ?? "",
          bid_increment: item.bid_increment || 50,
          position: item.position,
        })),
      });
      setLoading(false);
    });
  }, [auctionId, isEdit]);

  const searchProducts = async () => {
    try {
      const res = await productsAPI.getAll({
        search: productSearch,
        per_page: 10,
      });
      setProducts(res.data.data || []);
    } catch {
      toast.error("Product search failed");
    }
  };

  const addProduct = (p) => {
    if (form.items.some((i) => i.product_id === p.id)) {
      toast.info("Product already added");
      return;
    }
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          product_id: p.id,
          product_title: p.title,
          starting_price: p.price || 500,
          reserve_price: "",
          bid_increment: 50,
          position: f.items.length,
        },
      ],
    }));
    toast.success(`Added "${p.title}"`);
  };

  const removeItem = (idx) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) {
      toast.error("Add at least one product before saving.");
      return;
    }

    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      scheduled_at: form.scheduled_at || null,
      ends_at: form.ends_at || null,
      cover_image: form.cover_image,
      is_visible_on_app: form.is_visible_on_app,
      items: normalizeItems(form.items),
    };

    try {
      if (isEdit) {
        await auctionsAPI.update(auctionId, payload);
      } else {
        await auctionsAPI.create(payload);
      }
      toast.success("Auction saved with " + payload.items.length + " item(s)");
      navigate("/auctions");
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        toast.error(Object.values(errors).flat().join(" · "));
      } else {
        toast.error(err.response?.data?.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminSubpage title="Edit auction" backTo="/auctions">
        <div className="loading">Loading…</div>
      </AdminSubpage>
    );
  }

  return (
    <AdminSubpage
      title={isEdit ? "Edit auction" : "Create auction"}
      backTo="/auctions"
    >
      <div className="admin-page-panel modal-content">
        {form.items.length === 0 ? (
          <div className="auction-alert auction-alert-warning">
            Add at least one product and click <strong>Save auction</strong>{" "}
            before going live.
          </div>
        ) : (
          <div className="auction-alert auction-alert-info">
            {form.items.length} item(s) in this auction. Save before opening the
            Live control room.
          </div>
        )}

        <form onSubmit={save}>
          <div className="form-group">
            <label>Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={4}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Scheduled at</label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) =>
                  setForm({ ...form, scheduled_at: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Ends at</label>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Cover image URL</label>
            <input
              value={form.cover_image}
              onChange={(e) =>
                setForm({ ...form, cover_image: e.target.value })
              }
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label className="auction-visibility-toggle">
              <input
                type="checkbox"
                checked={form.is_visible_on_app}
                onChange={(e) =>
                  setForm({ ...form, is_visible_on_app: e.target.checked })
                }
              />
              Visible on mobile app
            </label>
          </div>

          <h3 className="section-title">Auction items</h3>

          <div className="auctions-toolbar">
            <input
              className="auction-reject-input"
              style={{ flex: 1, minWidth: 200 }}
              placeholder="Search products by name"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && (e.preventDefault(), searchProducts())
              }
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={searchProducts}
            >
              Search
            </button>
          </div>

          {products.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="auction-product-pick"
                  onClick={() => addProduct(p)}
                >
                  + {p.title} — AED {p.price}
                </button>
              ))}
            </div>
          ) : null}

          {form.items.map((item, idx) => (
            <div key={item.product_id} className="auction-item-card">
              <h4>{item.product_title || item.product_id}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Starting price (AED)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.starting_price}
                    onChange={(e) => {
                      const items = [...form.items];
                      items[idx].starting_price = e.target.value;
                      setForm({ ...form, items });
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Reserve price (hidden, optional)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.reserve_price}
                    onChange={(e) => {
                      const items = [...form.items];
                      items[idx].reserve_price = e.target.value;
                      setForm({ ...form, items });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Bid increment (AED)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.bid_increment}
                    onChange={(e) => {
                      const items = [...form.items];
                      items[idx].bid_increment = e.target.value;
                      setForm({ ...form, items });
                    }}
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeItem(idx)}
              >
                Remove
              </button>
            </div>
          ))}

          <div className="form-actions" style={{ marginTop: 20 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save auction"}
            </button>
            {isEdit ? (
              <Link
                to={`/auctions/${auctionId}/live`}
                className="btn btn-secondary"
                style={{ marginLeft: 8 }}
              >
                Open live control →
              </Link>
            ) : null}
          </div>
        </form>
      </div>
    </AdminSubpage>
  );
};

export default AuctionFormPage;
